/**
 * Sidebar.jsx
 * ===========
 * Shows selected parcel info and district > taluk > village navigation context.
 */

import React from 'react';
import { CROP_COLORS, STAGE_COLORS } from './MapDashboard';

const ADMIN_HIERARCHY = {
  Thiruvarur: {
    Needamangalam: ['Edakelayur', 'Valangaiman', 'Koothanallur', 'Thiruthuraipoondi'],
    Mannargudi:    ['Mannargudi', 'Thittachery', 'Veppathur'],
    Papanasam:     ['Papanasam', 'Thiruvidaimarudur'],
  },
  Thanjavur: {
    Thanjavur:   ['Thanjavur North', 'Thanjavur South', 'Pillaiyarpatti'],
    Kumbakonam:  ['Kumbakonam', 'Papanasam', 'Nachiyarkoil'],
    Pattukkottai:['Pattukkottai', 'Peravurani'],
  },
};

export default function Sidebar({ filters, selectedParcel, onFlyTo }) {
  return (
    <div className="space-y-3">

      {/* ── Admin hierarchy card ────────────────────────────────────────── */}
      <div className="glass rounded-lg p-3">
        <div className="text-xs font-bold text-green-400 mb-2 uppercase tracking-wider">
          📍 Selected Area
        </div>
        <div className="space-y-1">
          <HierarchyRow icon="🏛️" label="District" value={filters.district} />
          <HierarchyRow icon="🗺️" label="Taluk"    value={filters.taluk} />
          <HierarchyRow icon="🏘️" label="Village"  value={filters.village} />
          {filters.surveyNumber && (
            <HierarchyRow icon="📋" label="Survey#" value={filters.surveyNumber} />
          )}
        </div>

        {/* Village list */}
        {ADMIN_HIERARCHY[filters.district]?.[filters.taluk] && (
          <div className="mt-2 pt-2 border-t border-slate-700/50">
            <div className="text-xs text-slate-500 mb-1">Villages in {filters.taluk}:</div>
            <div className="flex flex-wrap gap-1">
              {ADMIN_HIERARCHY[filters.district][filters.taluk].map(v => (
                <span
                  key={v}
                  className={`text-xs px-2 py-0.5 rounded-full cursor-pointer transition-colors ${
                    v === filters.village
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {v}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Selected parcel detail ──────────────────────────────────────── */}
      {selectedParcel ? (
        <SelectedParcelCard parcel={selectedParcel} />
      ) : (
        <div className="glass rounded-lg p-3 text-center">
          <div className="text-2xl mb-1">🖱️</div>
          <div className="text-xs text-slate-400">
            Click a parcel on the map to see details
          </div>
        </div>
      )}
    </div>
  );
}

function HierarchyRow({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-500">{icon} {label}</span>
      <span className="text-slate-200 font-medium">{value || '—'}</span>
    </div>
  );
}

function SelectedParcelCard({ parcel }) {
  const cropColor  = CROP_COLORS[parcel.crop_type]     || '#94a3b8';
  const stageColor = STAGE_COLORS[parcel.growth_stage] || '#94a3b8';

  const ndvi = parcel.ndvi || 0;
  const ndviPct = Math.min(ndvi * 100, 100);
  const ndviColor = ndvi > 0.5 ? '#4ade80' : ndvi > 0.2 ? '#fbbf24' : '#f87171';

  return (
    <div
      className="rounded-lg p-3 border fade-in"
      style={{ borderColor: cropColor + '60', background: cropColor + '10' }}
    >
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-700/50">
        <div
          className="w-3 h-3 rounded-sm flex-shrink-0"
          style={{ background: cropColor }}
        />
        <div>
          <div className="text-sm font-bold text-white">
            Survey No. {parcel.survey_number}
          </div>
          <div className="text-xs text-slate-400">{parcel.village}</div>
        </div>
      </div>

      <div className="space-y-1.5 text-xs">
        {parcel.owner && (
          <DetailRow label="Owner" value={parcel.owner} />
        )}
        <DetailRow label="Crop Type"    value={parcel.crop_type}    color={cropColor}  bold />
        <DetailRow label="Growth Stage" value={parcel.growth_stage} color={stageColor} />
        <DetailRow label="Confidence"
          value={`${Math.round((parcel.confidence || 0) * 100)}%`}
          color={parcel.confidence >= 0.8 ? '#4ade80' : '#fbbf24'}
        />
        <DetailRow label="Area"         value={`${parcel.area_hectares} ha`} />
        {parcel.season && <DetailRow label="Season" value={parcel.season} />}
      </div>

      {/* NDVI gauge */}
      <div className="mt-3">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-slate-400">NDVI Index</span>
          <span style={{ color: ndviColor }} className="font-bold">
            {ndvi.toFixed(3)}
          </span>
        </div>
        <div className="h-3 rounded-full bg-slate-800 overflow-hidden relative">
          <div
            className="h-full rounded-full"
            style={{
              width: `${ndviPct}%`,
              background: 'linear-gradient(90deg,#ef4444,#f59e0b,#22c55e,#166534)',
              transition: 'width 0.5s ease',
            }}
          />
          {/* Threshold markers */}
          {[20, 40, 60].map(pct => (
            <div
              key={pct}
              className="absolute top-0 h-full w-px bg-slate-600"
              style={{ left: `${pct}%` }}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-slate-600 mt-0.5">
          <span>Bare</span>
          <span>Early</span>
          <span>Mid</span>
          <span>Mature</span>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, color, bold }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-400">{label}</span>
      <span
        className={bold ? 'font-bold' : 'font-medium'}
        style={{ color: color || '#e2e8f0' }}
      >
        {value}
      </span>
    </div>
  );
}
